/**
 * Double Exponential Moving Average (DEMA)
 *
 * Reduces lag compared to a single EMA by applying EMA twice:
 * DEMA = 2 × EMA₁ − EMA(EMA₁).
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeEMA } from "./sma";

export const demaIndicator: IndicatorDefinition = {
  id: "dema",
  name: "Double Exponential Moving Average",
  shortName: "DEMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
      { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#FF6D00" },
  ],
  plots: [
    { key: "dema", label: "DEMA", color: "#FF6D00", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const ema1 = computeEMA(src, period);
    const ema2 = computeEMA(ema1.filter(v => !isNaN(v)), period);

    const data: { time: number; value: number }[] = [];
    // ema2 is shorter — align with candles
    const ema1Start = period - 1;
    const ema2Start = ema1Start + period - 1;

    let ema2Idx = 0;
    for (let i = ema2Start; i < candles.length; i++) {
      if (ema2Idx < ema2.length && !isNaN(ema2[ema2Idx])) {
        const dema = 2 * ema1[i] - ema2[ema2Idx];
        data.push({ time: candles[i].time, value: dema });
      }
      ema2Idx++;
    }
    return [{ key: "dema", data }];
  },
};
